import logger from '@/utils/logger'
import {
  getTelegramChromeBackgroundColor,
  getTelegramHomeChromeColor,
  syncTelegramThemeCssVars,
} from '@/utils/telegramTheme'
const BODY_CLASS_FULLSCREEN = 'tg-fullscreen'

const DEFAULT_VIEWPORT_HEIGHT = '100dvh'
const TG_FULLSCREEN_CHROME_EXTRA = 50
const MIN_FULLSCREEN_TOP = 114
const THEME_COLOR_DARK = '#111827'
const MIN_FULLSCREEN_BOTTOM = 12
const MOBILE_TELEGRAM_PLATFORMS = new Set(['ios', 'android', 'android_x'])
const DESKTOP_TELEGRAM_PLATFORMS = new Set(['tdesktop', 'macos', 'web', 'weba', 'webk'])
const BODY_CLASS_TG_MOBILE = 'tg-platform-mobile'
const BODY_CLASS_TG_DESKTOP = 'tg-platform-desktop'

let activeTeardown = null
let syncTimers = []
let verticalSwipesDisabled = false

function getInsetValues(inset = {}) {
  return {
    top: Number(inset.top) || 0,
    right: Number(inset.right) || 0,
    bottom: Number(inset.bottom) || 0,
    left: Number(inset.left) || 0,
  }
}

function resolveInsets(tg) {
  const reported = getInsetValues(tg.contentSafeAreaInset)
  const safe = getInsetValues(tg.safeAreaInset)

  if (!tg.isFullscreen) {
    const useDesktopLayoutInsets = isTelegramDesktopPlatform(tg)
    return {
      top: useDesktopLayoutInsets ? 0 : Math.max(reported.top, safe.top),
      right: Math.max(reported.right, safe.right),
      bottom: Math.max(reported.bottom, safe.bottom),
      left: Math.max(reported.left, safe.left),
      headerPadLeft: Math.max(reported.left, safe.left),
      headerPadRight: Math.max(reported.right, safe.right),
    }
  }

  const top = Math.max(
    reported.top,
    safe.top + TG_FULLSCREEN_CHROME_EXTRA,
    MIN_FULLSCREEN_TOP,
  )

  return {
    top,
    right: Math.max(reported.right, safe.right),
    bottom: Math.max(reported.bottom, safe.bottom, MIN_FULLSCREEN_BOTTOM),
    left: Math.max(reported.left, safe.left),
    headerPadLeft: Math.max(reported.left, safe.left),
    headerPadRight: Math.max(reported.right, safe.right),
  }
}

function getViewportHeightValue(value) {
  const height = Number(value)
  return Number.isFinite(height) && height > 0 ? `${height}px` : DEFAULT_VIEWPORT_HEIGHT
}

export function getTelegramPlatform(tg = window.Telegram?.WebApp) {
  return String(tg?.platform || '').trim().toLowerCase()
}

export function isMobileLikeViewport() {
  const hasTouch = Number(navigator.maxTouchPoints) > 0
  const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches
  const mobileUserAgent = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
  return !!(mobileUserAgent || (hasTouch && coarsePointer))
}

export function isTelegramDesktopPlatform(tg = window.Telegram?.WebApp) {
  return DESKTOP_TELEGRAM_PLATFORMS.has(getTelegramPlatform(tg))
}

export function isTelegramMobilePlatform(tg = window.Telegram?.WebApp) {
  const platform = getTelegramPlatform(tg)
  if (MOBILE_TELEGRAM_PLATFORMS.has(platform)) return true
  if (DESKTOP_TELEGRAM_PLATFORMS.has(platform)) return false
  return isMobileLikeViewport()
}

export function isTelegramIosPlatform(tg = window.Telegram?.WebApp) {
  const platform = getTelegramPlatform(tg)
  if (platform === 'ios') return true
  if (platform) return false
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || '')
}

export function shouldUseTelegramFullscreen(tg = window.Telegram?.WebApp) {
  return isTelegramMobilePlatform(tg)
}

export function shouldExpandTelegramViewport(tg = window.Telegram?.WebApp) {
  return isTelegramMobilePlatform(tg)
}

function syncTelegramPlatformClass(tg) {
  const isMobile = isTelegramMobilePlatform(tg)
  const isDesktop = !isMobile
  document.body.classList.toggle(BODY_CLASS_TG_MOBILE, isMobile)
  document.documentElement.classList.toggle(BODY_CLASS_TG_MOBILE, isMobile)
  document.body.classList.toggle(BODY_CLASS_TG_DESKTOP, isDesktop)
  document.documentElement.classList.toggle(BODY_CLASS_TG_DESKTOP, isDesktop)
}

function applySafeAreaInsets(tg) {
  const safe = getInsetValues(tg.safeAreaInset)
  const content = resolveInsets(tg)
  const root = document.documentElement
  const fullscreenChromeTop = Math.max(0, content.top - safe.top)

  root.style.setProperty('--tg-safe-top', `${safe.top}px`)
  root.style.setProperty('--tg-safe-right', `${safe.right}px`)
  root.style.setProperty('--tg-safe-bottom', `${safe.bottom}px`)
  root.style.setProperty('--tg-safe-left', `${safe.left}px`)

  root.style.setProperty('--tg-content-safe-top', `${content.top}px`)
  root.style.setProperty('--tg-content-safe-right', `${content.right}px`)
  root.style.setProperty('--tg-content-safe-bottom', `${content.bottom}px`)
  root.style.setProperty('--tg-content-safe-left', `${content.left}px`)
  root.style.setProperty('--tg-header-pad-left', `${content.headerPadLeft}px`)
  root.style.setProperty('--tg-header-pad-right', `${content.headerPadRight}px`)
  root.style.setProperty('--tg-chrome-top', `${fullscreenChromeTop}px`)
  root.style.setProperty('--tg-viewport-height', getViewportHeightValue(tg.viewportHeight))
  root.style.setProperty('--tg-viewport-stable-height', getViewportHeightValue(tg.viewportStableHeight))

  root.style.setProperty('--app-left', '0px')
  root.style.setProperty('--app-width', '100%')
  root.style.setProperty('--app-right', '0px')
}

function syncFullscreenClass(tg) {
  syncTelegramPlatformClass(tg)
  const isFullscreen = shouldUseTelegramFullscreen(tg) && !!tg.isFullscreen
  document.body.classList.toggle(BODY_CLASS_FULLSCREEN, isFullscreen)
  document.documentElement.classList.toggle(BODY_CLASS_FULLSCREEN, isFullscreen)
  window.dispatchEvent(new Event('tg-app-frame-sync'))
}

function clearSyncTimers() {
  syncTimers.forEach((id) => clearTimeout(id))
  syncTimers = []
}

function scheduleSafeAreaSync(tg) {
  clearSyncTimers()
  const sync = () => {
    applySafeAreaInsets(tg)
    syncFullscreenClass(tg)
  }

  sync()
  requestAnimationFrame(sync)
  ;[50, 150, 350, 700, 1200].forEach((delay) => {
    syncTimers.push(setTimeout(sync, delay))
  })
}

function isDarkThemeActive() {
  return document.documentElement.classList.contains('dark')
}

function isTelegramHomePage() {
  const path = String(window.location?.pathname || '/').replace(/\/+$/, '') || '/'
  return path === '/'
}

export function getTelegramHeaderColor(tg = window.Telegram?.WebApp) {
  const isDark = isDarkThemeActive()
  syncTelegramThemeCssVars({ isDark })

  if (isDark) {
    return THEME_COLOR_DARK
  }

  // Fullscreen header is drawn by the app; match native chrome to the light header/safe-area
  // background so iOS status bar icons stay dark and readable.
  if (tg && shouldUseTelegramFullscreen(tg) && tg.isFullscreen) {
    return getTelegramHomeChromeColor(false)
  }

  const themeHeader = tg?.themeParams?.header_bg_color
  if (typeof themeHeader === 'string' && /^#[0-9A-Fa-f]{6}$/.test(themeHeader)) {
    return themeHeader
  }

  return getTelegramHomeChromeColor(false)
}

export function getTelegramBackgroundColor() {
  const isDark = isDarkThemeActive()
  syncTelegramThemeCssVars({ isDark })

  if (isDark) {
    return THEME_COLOR_DARK
  }

  if (isTelegramHomePage()) {
    return getTelegramChromeBackgroundColor(false)
  }

  const themeBg = tgBackgroundFromThemeParams()
  return themeBg || '#ffffff'
}

function tgBackgroundFromThemeParams() {
  const bg = window.Telegram?.WebApp?.themeParams?.bg_color
  if (typeof bg === 'string' && /^#[0-9A-Fa-f]{6}$/.test(bg)) {
    return bg
  }
  return null
}

export function updateTelegramHeaderColor() {
  const tg = window.Telegram?.WebApp
  if (!tg?.setHeaderColor) return

  try {
    const headerColor = getTelegramHeaderColor(tg)
    const backgroundColor = getTelegramBackgroundColor()
    tg.setHeaderColor(headerColor)
    tg.setBackgroundColor?.(backgroundColor)
    tg.setBottomBarColor?.(backgroundColor)
  } catch (error) {
    logger.warn('setHeaderColor not available:', error)
  }
}

function prepareTelegramViewport(tg) {
  const useFullscreen = shouldUseTelegramFullscreen(tg)

  if (!useFullscreen) {
    if (tg.isFullscreen && typeof tg.exitFullscreen === 'function') {
      try {
        tg.exitFullscreen()
      } catch (error) {
        logger.warn('exitFullscreen failed:', error)
      }
    }
    return
  }

  if (typeof tg.isVersionAtLeast !== 'function' || !tg.isVersionAtLeast('8.0')) {
    return
  }

  try {
    tg.disableVerticalSwipes?.()
    verticalSwipesDisabled = true
  } catch (error) {
    logger.warn('disableVerticalSwipes failed:', error)
  }

  if (typeof tg.requestFullscreen !== 'function' || tg.isFullscreen) {
    return
  }

  try {
    tg.requestFullscreen()
  } catch (error) {
    logger.warn('requestFullscreen failed:', error)
  }
}

/**
 * Enable Telegram Mini App fullscreen mode and keep safe-area CSS vars in sync.
 * @param {import('telegram-web-app').WebApp} tg
 * @returns {() => void} teardown
 */
export function initTelegramViewport(tg) {
  if (activeTeardown) {
    activeTeardown()
    activeTeardown = null
  }

  if (!tg) return () => {}

  if (tg.initData) {
    document.body.classList.add('tg-webapp')
    document.documentElement.classList.add('tg-webapp')
  }

  const handlers = []

  const bind = (event, handler) => {
    if (!tg.onEvent) return
    tg.onEvent(event, handler)
    handlers.push({ event, handler })
  }

  const onViewportChange = () => {
    syncTelegramThemeCssVars()
    scheduleSafeAreaSync(tg)
    updateTelegramHeaderColor()
  }

  syncTelegramThemeCssVars()
  scheduleSafeAreaSync(tg)
  updateTelegramHeaderColor()

  bind('safeAreaChanged', onViewportChange)
  bind('contentSafeAreaChanged', onViewportChange)
  bind('viewportChanged', onViewportChange)
  bind('fullscreenChanged', onViewportChange)
  bind('activated', onViewportChange)
  bind('fullscreenFailed', () => {
    logger.warn('Telegram fullscreen request failed')
  })

  prepareTelegramViewport(tg)
  scheduleSafeAreaSync(tg)

  const teardown = () => {
    clearSyncTimers()
    handlers.forEach(({ event, handler }) => {
      tg.offEvent?.(event, handler)
    })
    if (verticalSwipesDisabled) {
      try {
        tg.enableVerticalSwipes?.()
      } catch (error) {
        logger.warn('enableVerticalSwipes failed:', error)
      }
      verticalSwipesDisabled = false
    }
    document.body.classList.remove(BODY_CLASS_FULLSCREEN)
    document.documentElement.classList.remove(BODY_CLASS_FULLSCREEN)
    document.body.classList.remove(BODY_CLASS_TG_MOBILE, BODY_CLASS_TG_DESKTOP)
    document.documentElement.classList.remove(BODY_CLASS_TG_MOBILE, BODY_CLASS_TG_DESKTOP)
    document.body.classList.remove('tg-webapp')
    document.documentElement.classList.remove('tg-webapp')
    const root = document.documentElement
    root.style.removeProperty('--tg-chrome-top')
    root.style.removeProperty('--tg-viewport-height')
    root.style.removeProperty('--tg-viewport-stable-height')
    activeTeardown = null
  }

  activeTeardown = teardown
  return teardown
}

export function destroyTelegramViewport() {
  if (activeTeardown) {
    activeTeardown()
  }
  clearSyncTimers()
}
